/**
 * Micronutrients (vitamins & minerals) tracked as an optional premium nutrient set.
 *
 * Keys carry their unit suffix so values are never ambiguous across the AI
 * contract, Firestore, and the UI. Daily values follow the FDA adult DV table
 * (21 CFR 101.9) and are used only for the "% Daily Value" reference bar.
 *
 * Keep in sync with functions/src/prompts/shared.ts (MICRO_KEYS).
 */

export const MICRO_KEYS = [
  'sodiumMg',
  'potassiumMg',
  'calciumMg',
  'ironMg',
  'magnesiumMg',
  'zincMg',
  'vitaminAMcg',
  'vitaminCMg',
  'vitaminDMcg',
  'vitaminB12Mcg',
  'folateMcg',
  'cholesterolMg',
  'saturatedFatG',
] as const;

export type MicroKey = (typeof MICRO_KEYS)[number];

/** Partial map of micronutrient values for a food, meal, or day. */
export type Micronutrients = Partial<Record<MicroKey, number>>;

export const MICRO_UNITS: Record<MicroKey, 'mg' | 'mcg' | 'g'> = {
  sodiumMg: 'mg',
  potassiumMg: 'mg',
  calciumMg: 'mg',
  ironMg: 'mg',
  magnesiumMg: 'mg',
  zincMg: 'mg',
  vitaminAMcg: 'mcg',
  vitaminCMg: 'mg',
  vitaminDMcg: 'mcg',
  vitaminB12Mcg: 'mcg',
  folateMcg: 'mcg',
  cholesterolMg: 'mg',
  saturatedFatG: 'g',
};

/** FDA adult Daily Values (reference amounts for the %DV bar). */
export const MICRO_DAILY_VALUES: Record<MicroKey, number> = {
  sodiumMg: 2300,
  potassiumMg: 4700,
  calciumMg: 1300,
  ironMg: 18,
  magnesiumMg: 420,
  zincMg: 11,
  vitaminAMcg: 900,
  vitaminCMg: 90,
  vitaminDMcg: 20,
  vitaminB12Mcg: 2.4,
  folateMcg: 400,
  cholesterolMg: 300,
  saturatedFatG: 20,
};

/** Nutrients where exceeding the DV is undesirable (bar turns warning-colored). */
export const MICRO_LIMIT_KEYS: ReadonlySet<MicroKey> = new Set<MicroKey>([
  'sodiumMg',
  'cholesterolMg',
  'saturatedFatG',
]);

/** Sum micronutrient maps (undefined-safe); returns undefined when nothing to sum. */
export function sumMicros(items: Array<Micronutrients | undefined>): Micronutrients | undefined {
  const out: Micronutrients = {};
  let any = false;
  for (const m of items) {
    if (!m) continue;
    for (const k of MICRO_KEYS) {
      const v = m[k];
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
        out[k] = (out[k] || 0) + v;
        any = true;
      }
    }
  }
  return any ? out : undefined;
}

/** Scale a micronutrient map by a multiplier (servings). */
export function scaleMicros(m: Micronutrients | undefined, factor: number): Micronutrients | undefined {
  if (!m) return undefined;
  const out: Micronutrients = {};
  let any = false;
  for (const k of MICRO_KEYS) {
    const v = m[k];
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
      out[k] = Math.round(v * factor * 10) / 10;
      any = true;
    }
  }
  return any ? out : undefined;
}

/** True when the map has at least one positive value. */
export function hasMicros(m: Micronutrients | undefined): boolean {
  return !!m && MICRO_KEYS.some((k) => typeof m[k] === 'number' && (m[k] as number) > 0);
}
